import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Row, Col, Accordion, AccordionItem, AccordionHeader, AccordionBody } from 'reactstrap';
import Body from '../Components/layout/Body';
import LearnMoreModal from '../Components/layout/LearnMoreModal';
import { TABS, SUBTABS } from './ConstPaths';
import { getIssueLabelForValue, getRemediesForValue, REMEDIES_OVERALL } from './ConstIssueBtns';
import { getStarNumberForValue, getSuccessNumberForValue } from './ConstRatingBtns';
import TestDriveDevSidebar from './TestDriveDevSidebar';

const TD_INTERPRET_DEEPER_HELP = `
  <div>
    <p>
    Here we try to give you some ideas that might inform your next steps.
    </p>
    <p>
    Under General Suggestions, you’ll see some ideas that apply to basically any type of situation and access method.  
    Use the help links provided to get more details.     
    </p>
    <p>
    The Specific Suggestions section depends on the issues you selected earlier. 
    For example, if you checked the box for ‘Maintaining attention during the tasks’ when rating the Test-drive, you’ll see some suggestions related to that here.  
    Some of these also have help links with more details, including videos.   
    </p> 
    <p><strong>Note: </strong>We know that there are usually no simple solutions to these issues, and we don’t want to pretend that any app can “know” what to do. 
     But we hope that the ideas and prompts here will help you and the team come up with some useful next steps. 
    </p>
  </div>
`;

const propTypes = {
  firstName: PropTypes.string,
  pronouns: PropTypes.arrayOf(PropTypes.string),
  activeAccessMethodCategory: PropTypes.string,
  activeTestDriveClientRating: PropTypes.string,
  activeTestDrivePractitionerRating: PropTypes.string,
  activeTestDriveIssuesNoted: PropTypes.arrayOf(PropTypes.string),
  onContinue: PropTypes.func.isRequired
};

const defaultProps = {
  firstName: 'the client',
  pronouns: ['they', 'them', 'their'],
  activeAccessMethodCategory: '',
  activeTestDriveClientRating: '',
  activeTestDrivePractitionerRating: '',
  activeTestDriveIssuesNoted: [],
};

//content sub-component with unique content for this page
function TestDriveInterpretDeeperContent(props) {
  const activeAccessMethodCategory = props.activeAccessMethodCategory;
  const activeTestDriveClientRating = props.activeTestDriveClientRating;
  const activeTestDrivePractitionerRating = props.activeTestDrivePractitionerRating;
  const issuesNoted = props.activeTestDriveIssuesNoted;

  // const numStars = getStarNumberForValue(activeTestDriveClientRating);
  // const successNumber = getSuccessNumberForValue(activeTestDrivePractitionerRating);

  //TODO this assumes the issues are from .any, but eventually this will change.
  return(
    <>
    {/* <QuickInterpretation numStars={numStars} successNumber={successNumber} /> */}
    <p>Here are some common suggestions for remedies and next steps, for the issues you observed. Browse through these ideas - not all will apply to your situations, but hopefully you'll find something helpful here.</p>
    {(!activeAccessMethodCategory)
      ? <p><strong>Note: </strong>Without knowing the access method used in this Test-drive, we can only offer very general suggestions here.</p>
      : null
    }
    <GeneralConsiderations remedies={REMEDIES_OVERALL} />
    <SpecificConsiderations issuesNoted={issuesNoted} />
    </>
  );
}

function GeneralConsiderations(props){
  const remedies = props.remedies;

  return(
    <>
    <div className="aa-callout">
      <h5>General Suggestions:</h5>
      <ul>
      {remedies.map(function(rem, remIndex){
        return(
          <li key={remIndex} className={rem.className}>
            {rem.label ? rem.label + ' ' : ''}
            <LearnMoreModal buttonClassName="aa-no-padding" 
              linkText={rem.linkText ? rem.linkText : ""} 
              title={rem.label ? rem.label : rem.linkText} 
              content={rem.help} />
          </li>
        );
      })}
      </ul>
    </div>
    </>
  );
}

function SpecificConsiderations(props){
  const issuesNoted = props.issuesNoted;
  //determine if there are any specific considerations:
  const firstItemWithContent = issuesNoted.findIndex((issue) => (getRemediesForValue(issue).length > 0));

  //defines initial open item, with firstItemWithContent being the top one
  //(the 0th issue may not have any remedies, so we don't want to assume that 0 is always the first issue with remedies.)
  const idPrefix = "item";
  const [open, setOpen] = useState(idPrefix+firstItemWithContent);
  const toggle = (id) => {
    open === id ? setOpen() : setOpen(id);
  };

  if(firstItemWithContent > -1){
  return(
    <>
    <h5>Specific Suggestions for issues you observed:</h5>
    <Accordion
      open={open} toggle={toggle} 
    >
    {issuesNoted.map(function(issue, index) {
      let remedies = getRemediesForValue(issue);
      let aIndex = "a"+index;
      if (remedies.length > 0) {
        return(
          <AccordionItem key={aIndex}>
            <AccordionHeader targetId={idPrefix+index}>
              {getIssueLabelForValue(issue)}
            </AccordionHeader>
            <AccordionBody accordionId={idPrefix+index}>
              <ul>
              {getRemediesForValue(issue).map(function(rem, remIndex){
                return(
                  <li key={remIndex} className={rem.className}>
                    {rem.label}{' '}
                    <LearnMoreModal buttonClassName="aa-no-padding" 
                      linkText={rem.linkText ? rem.linkText : ""} 
                      title={rem.label ? rem.label : rem.linkText} 
                      content={rem.help} 
                    />
                  </li>
                );
              })}
              </ul>
            </AccordionBody>
          </AccordionItem>
        );
      }
      else return (<></>);
    })}
    </Accordion>
    <p className="aa-more-info">* NOTE: You may not see suggestions for every issue you observed.</p>
    </>
  );
  }
  else return(<></>);
}

//This is currently not used cuz we didn't like it. 
function QuickInterpretation(props){
  const {
    numStars,
    successNumber
  } = props;

  const ratePath = SUBTABS.RATE;
  const incomplete = 
  <>
    <p>We don't have complete ratings for stars and success. 
      Visit the <Link to={ratePath}>Rate-it section</Link> if you'd like to finish your ratings.</p>
  </>;
  const poor = 
  <>
    <p>Ratings weren't too good for this Test-drive, so it may make sense to continue exploring solutions.</p>
  </>;
  const mixed = 
  <>
    <p>Ratings were mixed for this Test-drive, so it may make sense to continue exploring solutions.</p>
  </>;
  const good = 
  <>
    <p>This Test-drive had some good ratings! Decide together if you feel you've found a successful solution.</p>
  </>;

  if(!numStars || !successNumber){
    return(incomplete); 
  }
  else if(numStars<=2 && successNumber<=25){
    return(poor);
  }
  else if(numStars>=4 && successNumber>=75){
    return(good);
  }
  else{
    return(mixed);
  }
}

//main component for this page
//basic pattern is to set up the pageData, define the unique content for the page (above),
//then render the page using <Body> component
function TestDriveInterpretDeeper(props) {
  const firstName = props.firstName;
  const activeAccessMethodCategory = props.activeAccessMethodCategory;
  const activeTestDriveClientRating = props.activeTestDriveClientRating;
  const activeTestDrivePractitionerRating = props.activeTestDrivePractitionerRating;
  const activeTestDriveIssuesNoted = props.activeTestDriveIssuesNoted;
  const onContinue = props.onContinue;
  const dev = props.dev || {};

  const pageData = {
    image: "",
    faIcon: "",
    pageSymbolClassName: "aa-section-symbol test-drive",
    title: `Let's look a little deeper at this Test-drive`,
    subTitle: ``,
    learnMore: {linkText: "Learn More about interpreting Test-drives",
                title: "Interpreting Test-drive results",
                content: TD_INTERPRET_DEEPER_HELP
                },
    nextRoute: "", 
    sidebar: false
  }

  console.log("Rendering TestDriveInterpretDeeper");
  
  return(
    <>
    <Body page={pageData} 
      contentComponent={
        <TestDriveInterpretDeeperContent 
          activeAccessMethodCategory={activeAccessMethodCategory}
          activeTestDriveClientRating={activeTestDriveClientRating}
          activeTestDrivePractitionerRating={activeTestDrivePractitionerRating}
          activeTestDriveIssuesNoted={activeTestDriveIssuesNoted}
        />
      } 
      sidebarComponent={
        dev.showDevSidebar 
        &&
        <TestDriveDevSidebar 
          activeTestDriveId={dev.tdId}
          activeTestDrive={dev.td}
          activeTestDriveActivityId={dev.actId}
          activeTestDriveActivities={dev.acts}
          completedTestDrives={dev.ctds}
        />  
      }
      onContinue={onContinue}  
    />
    </>
  );
}

TestDriveInterpretDeeper.propTypes = propTypes;
TestDriveInterpretDeeper.defaultProps = defaultProps;

export default TestDriveInterpretDeeper;